// @givmo/app-charity — the ONE way a route lands a person INSIDE a named organization's console.
//
// Two doorways need it: the organization deep link (`router/organization-deep-link.tsx`, CPA-BE29 —
// the address the claim-approved email points at) and the claim wizard's entry
// (`features/claim/claim-wizard-entry.tsx`, CPA-WEB52 — a caller who opens the wizard for a charity
// they already administer). Both must do the same two things in the same order, and neither may
// spell the destination itself:
//
//   1. SELECT the organization through the active-org context's one writer (`setActiveCharityId`),
//      which persists the choice per user on this device — the same signature the switcher leaves;
//   2. once the selection has COMMITTED, forward to the doorway (`DEFAULT_AUTHENTICATED_ROUTE`, the
//      root), which runs the ONE landing rule for the newly active organization — its chosen default
//      module, the class default, the apps page. No second literal, no page named here. A caller
//      that was handed a DESTINATION (the deep link's `to`, CCM-R1-SIGN-LINK-ORG: a signing
//      email's request) forwards there instead, at the same moment and for the same reason: the
//      page then reads as the newly active organization, never the old one.
//
// Two steps across two renders, by construction: selecting re-keys `<OrgScopedBoundary>` and remounts
// everything under it, the hook re-runs with the target active, and only then does it navigate — so
// the doorway never computes the OLD organization's landing. A caller whose target is already the
// active organization takes step 2 straight away. The hook decides nothing about WHETHER to enter:
// `enabled` is the caller's verdict (a refreshed list that holds the charity; a cached list that
// holds it), made from the memberships read the caller already observes.

import { useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { DEFAULT_AUTHENTICATED_ROUTE } from '../features/auth/auth-routes';
import { useActiveOrg } from './active-org-context';

/**
 * Select `charityId` and land on its console's landing, or on `destination` when one is given (a
 * path in this console the caller has already vetted: `router/organization-deep-link.tsx`'s
 * `organizationDeepLinkDestination`). Inert while `enabled` is false; the caller must only enable it
 * for an organization the memberships read says the person holds — the context's writer ignores an
 * id the caller does not administer, and the hook would then wait forever.
 */
export function useEnterOrganization(
  charityId: string,
  enabled: boolean,
  destination?: string,
): void {
  const navigate = useNavigate();
  const { activeCharityId, setActiveCharityId } = useActiveOrg();

  useEffect(() => {
    if (!enabled) return;
    if (activeCharityId !== charityId) {
      // Step 1; the remount this causes re-runs the effect with the target active.
      setActiveCharityId(charityId);
      return;
    }
    if (destination !== undefined) {
      // `href`, not `to`: a destination may carry its own search, which `to` would read as path.
      void navigate({ href: destination, replace: true });
      return;
    }
    void navigate({ to: DEFAULT_AUTHENTICATED_ROUTE, replace: true });
  }, [activeCharityId, charityId, destination, enabled, navigate, setActiveCharityId]);
}
